import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  TextField,
} from '@mui/material';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { CloseIcon } from '~/assets/icons';
import { useFormik } from 'formik';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT, MAP_DEFAULT_LAT, MAP_DEFAULT_LONG } from '~/utils/constants/common';
import { cast, catchError, handleApiError, notify } from '~/utils/helper';
import { BaseAutocomplete, BaseDatePicker, BaseRow, BaseSelect, SelectAssigneeInput } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import dayjs from 'dayjs';
import styles from './EditTaskDialog.module.scss';
import { handleCreateTask, handleGetTaskFiles, handleUpdateTask } from '~/thunks/task/taskThunk';
import SelectTaskAction from '../job-detail/components/select-task-action/SelectTaskAction';
import jobApi from '~/apis/job';
import { IJob, IJobSelection } from '~/utils/interface/job';
import classNames from 'classnames/bind';
import { IEditTask } from '~/utils/interface/task';
import { taskActions } from '~/thunks/task/taskSlice';
import { Map, Marker } from '@vis.gl/react-google-maps';
import { handleGetGoogleSearch } from '~/thunks/google/googleThunk';
import { IGoogleSearch } from '~/utils/interface/google';
import { FILE_ITEM_FOR_TASK } from '~/utils/constants/jobs';
import FileItem from '~/components/estimate/file-item/FileItem';

const cx = classNames.bind(styles);
type EditTaskDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  mode?: string;
  title?: string;
  data?: string;
  job?: IJob;
  children?: React.ReactNode;
};

const initialValues: IEditTask = {
  name: '',
  description: '',
  dueDate: '',
  assigneeIds: [],
  files: undefined,
  jobFileIds: [],
};

const EditTaskDialog = (props: EditTaskDialogProps) => {
  //#region Destructuring Props
  const { mode, data, job } = props;
  //#endregion Destructuring Props
  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['admin/job', 'admin/common']);
  //#endregion Declare Hook
  //#region Selector
  const { selectAccounts } = useAppSelector((state) => state.account);
  const { needRefreshData } = useAppSelector((state) => state.job);
  const { options } = useAppSelector((state) => state.google);
  let { taskEdit } = useAppSelector((state) => state.task);
  //#endregion Selector
  //#region Declare State
  const [projectsSelection, setProjectsSelection] = useState<Array<IJobSelection>>([]);

  const statusOptions = useMemo(
    () => [
      {
        id: '1',
        name: t('selectStatus.statusTask.new', { ns: 'admin/component' }),
      },
      {
        id: '2',
        name: t('selectStatus.statusTask.progress', { ns: 'admin/component' }),
      },
      {
        id: '3',
        name: t('selectStatus.statusTask.completed', { ns: 'admin/component' }),
      },
    ],
    [t]
  );
  //#endregion Declare State
  //#region Implement Hook
  useEffect(() => {
    if (mode === DIALOG_MODE_EDIT && props.open) {
      form.setFieldValue('assigneeIds', cast(taskEdit?.assigneeIds?.[0]));
      form.setFieldValue('name', cast(taskEdit?.name));
      dayjs(taskEdit?.dueDate).isValid() &&
        form.setFieldValue('dueDate', cast(dayjs(taskEdit?.dueDate).format('YYYY-MM-DD HH:mm:ss')));
      form.setFieldValue('statusId', cast(taskEdit?.statusId));
      form.setFieldValue('description', cast(taskEdit?.description));
      form.setFieldValue('jobFileIds', cast(taskEdit?.jobFileIds));
      form.setFieldValue('lat', cast(taskEdit?.lat));
      form.setFieldValue('long', cast(taskEdit?.long));
      form.setFieldValue('jobId', cast(taskEdit?.jobId));

      form.setFieldValue('location', cast(taskEdit?.location));
    }
    if (mode === DIALOG_MODE_CREATE && props.open) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      taskEdit && dispatch(taskActions.changeOpenModalCreate());
      if (data) {
        form.setFieldValue('jobId', data);
        form.setFieldValue('location', job?.fullAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, taskEdit]);

  useEffect(() => {
    getJobSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshData]);

  useEffect(() => {
    if (data && props.open && mode === DIALOG_MODE_EDIT) {
      dispatch(handleGetTaskFiles({ id: data }))
        .unwrap()
        .catch((e) => {
          handleApiError(e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);
  //#endregion Implement Hook
  //#region Handle Function
  const handleCancel = () => {
    handleSetOpen(false);
    form.resetForm();
    dispatch(taskActions.changeMode(''));
  };
  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values, { setFieldError }) => {
      const updatedValues: IEditTask = {
        ...values,
        assigneeIds: Array.isArray(values.assigneeIds)
          ? values.assigneeIds.filter((id) => id !== undefined && id !== null)
          : values.assigneeIds
          ? [values.assigneeIds]
          : [],
      };

      if (mode === DIALOG_MODE_CREATE) {
        try {
          delete updatedValues.status;
          await dispatch(handleCreateTask({ data: updatedValues })).unwrap();
          notify('success', t('createSuccess', { ns: 'admin/common' }));
          await dispatch(taskActions.changeNeedRefreshData(true));
          handleSetOpen(false);
          await dispatch(taskActions.changeMode(''));
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
      if (mode === DIALOG_MODE_EDIT) {
        try {
          let updatedEditValues = updatedValues;
          if (form.values.files) {
            updatedEditValues = { ...updatedEditValues, jobFileIds: '' };
          }
          let newValues = updatedEditValues;
          await dispatch(handleUpdateTask({ id: taskEdit?.id, data: newValues })).unwrap();
          notify('success', t('editSuccess', { ns: 'admin/common' }));
          handleSetOpen(false);
          await dispatch(taskActions.changeNeedRefreshData(true));
          await dispatch(taskActions.changeMode(''));
        } catch (e: unknown) {
          handleApiError(e, setFieldError);
        }
        return;
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (mode === DIALOG_MODE_CREATE) {
      form.resetForm();
    }
    if (mode === DIALOG_MODE_EDIT) {
      form.resetForm();
    }
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getJobSelections = async () => {
    try {
      const response = await jobApi.getJobsSelection();
      setProjectsSelection(response.data.data.responses);
      await dispatch(taskActions.changeNeedRefreshData(true));
    } catch (error: unknown) {
      catchError(error, (message) => {
        notify('error', message);
      });
    }
  };

  const handleSearch = async (inputValue: string) => {
    dispatch(handleGetGoogleSearch({ query: inputValue }));
  };

  const handleSelect = (selectedOption: IGoogleSearch) => {
    form.setFieldValue('lat', selectedOption?.location.lat);
    form.setFieldValue('long', selectedOption?.location.long);
    form.setFieldValue('location', selectedOption?.name);
  };

  const handleChangeText = (text: string) => {
    form.setFieldValue('location', text);
  };

  const renderMarkerComponent = () => {
    return (
      <Marker
        position={{
          lat: form.values.lat ? Number(form.values.lat) : MAP_DEFAULT_LAT,
          lng: form.values.long ? Number(form.values.long) : MAP_DEFAULT_LONG,
        }}
        label={{
          text: form.values.location && form.values.location !== '' ? form.values.location : 'Select Location',
          className:
            'bg-[#FFFFFF] p-[8px] absolute bottom-[-10px] left-[-5px] rounded-b-[6px] rounded-tr-[6px] border-[0.79px] border-[#E5EAEF]',
        }}
      />
    );
  };

  //#endregion Handle Function
  return (
    <Dialog open={props.open} onClose={() => handleCancel()} fullWidth maxWidth='md'>
      <DialogTitle>
        {mode === DIALOG_MODE_CREATE ? t('addTaskDialog.addNewTask') : t('addTaskDialog.editTask')}
        <IconButton onClick={() => handleCancel()} className='absolute right-[10px] top-[10px]'>
          <CloseIcon className='w-[10px] h-[10px]' />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.assignedStaff')}</InputLabel>
              <SelectAssigneeInput
                name='assigneeIds'
                value={form.values?.assigneeIds}
                options={selectAccounts}
                onChange={form.handleChange}
                optionInput={true}
                helperText={form.errors.assigneeIds}
              />
            </Box>

            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.taskAction')}</InputLabel>
              <SelectTaskAction
                name='name'
                value={form.values.name}
                onChange={form.handleChange}
                helperText={form.errors.name}
                error={Boolean(form.errors?.name)}
              ></SelectTaskAction>
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[20px]'>
            {mode === DIALOG_MODE_EDIT ? (
              <Box className='w-full '>
                <InputLabel>{t('addTaskDialog.status')}</InputLabel>
                <BaseSelect
                  options={statusOptions}
                  name='statusId'
                  isHideIconCheckbox={true}
                  value={form.values.statusId}
                  onChange={(e) => {
                    form.setFieldValue('statusId', e.target.value);
                  }}
                  error={Boolean(form.errors?.statusId)}
                  helperText={form.errors?.statusId}
                />
              </Box>
            ) : (
              <></>
            )}

            <Box className='w-full '>
              <InputLabel>{t('addTaskDialog.duedate')}</InputLabel>
              <BaseDatePicker
                placeholder={t('addTaskDialog.duedate')}
                value={form.values.dueDate}
                dateFormat='DD MMM, YYYY hh:mm a'
                onChange={(date) => {
                  if (date) {
                    const selectedDate = dayjs(date).format('YYYY-MM-DD HH:mm:ss');
                    form.setFieldValue('dueDate', selectedDate);
                  }
                }}
                error={Boolean(form.errors.dueDate) || Boolean(form.errors.dueDate)}
                helperText={form.errors.dueDate || form.errors.dueDate}
                showTimeInput
              />
            </Box>
          </BaseRow>
          <BaseRow className='gap-[20px] mb-[20px]'>
            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.note')}</InputLabel>
              <TextField
                name='description'
                placeholder={t('addTaskDialog.note')}
                value={form.values?.description || ''}
                onChange={form.handleChange}
                error={Boolean(form.errors?.description)}
                helperText={form.errors?.description}
                multiline
              />
            </Box>
          </BaseRow>

          <BaseRow className='gap-[20px] mb-[10px]'>
            <Box className='w-full'>
              <InputLabel>{t('addTaskDialog.project')}</InputLabel>
              <BaseSelect
                options={projectsSelection}
                name='jobId'
                disabled={!!data}
                isHideIconCheckbox={true}
                label={t('addTaskDialog.labelProject')}
                value={form.values?.jobId}
                onChange={form.handleChange}
                error={Boolean(form.errors?.jobId)}
                helperText={form.errors?.jobId}
              />
            </Box>
          </BaseRow>

          <BaseRow className='mb-[20px] mt-[20px]'>
            <Box className={cx('box-input')}>
              <InputLabel>{t('addTaskDialog.location')}</InputLabel>
              <BaseAutocomplete
                onSearch={handleSearch}
                onSelected={handleSelect}
                onChangeText={handleChangeText}
                options={options}
                value={
                  form.values.location
                    ? {
                        id: '1',
                        name: form.values.location,
                        group: 'Location',
                      }
                    : null
                }
              />
            </Box>
          </BaseRow>
          <Box className='mb-[20px] mt-[20px]'>
            <InputLabel>{t('addTaskDialog.mapView')}</InputLabel>
            <Box>
              <Map
                className='h-[400px] w-full'
                center={{
                  lat: form.values.lat ? Number(form.values.lat) : MAP_DEFAULT_LAT,
                  lng: form.values.long ? Number(form.values.long) : MAP_DEFAULT_LONG,
                }}
                defaultZoom={5}
                gestureHandling={'greedy'}
                disableDefaultUI={true}
                mapId={process.env.REACT_APP_GOOGLE_MAPS_ID}
              >
                {renderMarkerComponent()}
              </Map>
            </Box>
          </Box>
        </Box>
        {form.values.jobId && (
          <Box>
            <FileItem jobId={form.values.jobId ?? ''} form={form} typeData={FILE_ITEM_FOR_TASK} editData={taskEdit} />
          </Box>
        )}
      </DialogContent>

      <DialogActions className='justify-end'>
        <Button color='secondary' variant='contained' onClick={() => handleCancel()}>
          {t('cancel', { ns: 'admin/common' })}
        </Button>
        <Button
          color='primary'
          variant='contained'
          onClick={() => {
            form.handleSubmit();
          }}
        >
          {t('save', { ns: 'admin/common' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(EditTaskDialog);
